// backgroundTaskStore.ts — Globaali taustatehtävien tila
// Tehtävät säilyvät navigoinnin + selaimen sulkemisen yli (localStorage)

import { useState, useEffect } from 'react';

const KEY = 'savroc_background_tasks';

export interface BackgroundTask {
  id: string;
  type: 'coating-timer' | 'xrf-wait' | 'bath-check' | 'generic';
  label: string;          // "Kromi sykli 2 — Kylpy 35"
  startedAt: number;      // Date.now()
  durationSeconds?: number; // null = ei ajastinta (vain tila)
  route: string;          // "/op/work" — mihin palataan
  stateKey?: string;      // localStorage-avain jolla tila palautetaan
  status: 'running' | 'done' | 'dismissed';
}

export function getTasks(): BackgroundTask[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '[]'); } catch { return []; }
}

function saveTasks(tasks: BackgroundTask[]) {
  localStorage.setItem(KEY, JSON.stringify(tasks));
  window.dispatchEvent(new Event('background-tasks-change'));
}

export function addTask(task: Omit<BackgroundTask, 'status'>): BackgroundTask {
  const full: BackgroundTask = { ...task, status: 'running' };
  const tasks = getTasks().filter(t => t.id !== task.id); // Korvaa sama id
  tasks.push(full);
  saveTasks(tasks);
  return full;
}

export function removeTask(id: string) {
  saveTasks(getTasks().filter(t => t.id !== id));
}

export function markDone(id: string) {
  const tasks = getTasks().map(t => t.id === id ? { ...t, status: 'done' as const } : t);
  saveTasks(tasks);
}

export function dismissTask(id: string) {
  saveTasks(getTasks().filter(t => t.id !== id));
}

export function useBackgroundTasks(): BackgroundTask[] {
  const [tasks, setTasks] = useState<BackgroundTask[]>(getTasks);

  useEffect(() => {
    const handler = () => setTasks(getTasks());
    window.addEventListener('background-tasks-change', handler);
    window.addEventListener('storage', handler);

    // Tarkista ajastimet joka sekunti
    const interval = setInterval(() => {
      const current = getTasks();
      let changed = false;
      for (const t of current) {
        if (t.status === 'running' && t.durationSeconds) {
          const elapsed = (Date.now() - t.startedAt) / 1000;
          if (elapsed >= t.durationSeconds && t.status === 'running') {
            t.status = 'done';
            changed = true;
            // Äänimerkki + vibra
            try { navigator.vibrate?.([200, 100, 200]); } catch {}
            try {
              if (Notification.permission === 'granted') {
                new Notification(`⏰ ${t.label}`, { body: 'Ajastin valmis!', icon: '/favicon.svg' });
              }
            } catch {}
          }
        }
      }
      if (changed) saveTasks(current);
      setTasks(current);
    }, 1000);

    return () => {
      window.removeEventListener('background-tasks-change', handler);
      window.removeEventListener('storage', handler);
      clearInterval(interval);
    };
  }, []);

  return tasks;
}

export function getRemaining(task: BackgroundTask): number {
  if (!task.durationSeconds) return -1;
  return Math.max(0, task.durationSeconds - (Date.now() - task.startedAt) / 1000);
}

export function formatTimer(secs: number): string {
  if (secs < 0) return '';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
